import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import React, { useEffect, useRef, useState } from "react";
import { Search } from "../../shared/Search";
import { EmptyList, TListState } from "../../shared/modal";
import { EmptyPipFilter, SubStatusType, TPipFilter, TPipeline } from "./modal";
import { Fetch, ILazyParams } from "../../utilities/fetch";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Filter } from "./Filter";
import { format } from "date-fns";
import { RequirementInfo } from "./RequirementInfo";
import { ChangeStatus } from "./ChangeStatus";
import { Dialog } from "primereact/dialog";
import { Form } from "../eci/Form";
import { EmptyECI } from "../eci/modal";
import { PipelineHistory } from "./History";

type TL = TListState<TPipeline>;

export const Table = () => {
    const [table, setTable] = useState(EmptyList as TL);
    const [globalSearch, setGlobalSearch] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });
    const [filters, setFilters] = useState<TPipFilter>(EmptyPipFilter);

    const [requirementId, setRequirementId] = useState<number | null>(null);

    const [upStatus, setUpStatus] = useState({ open: false, data: undefined } as { open: boolean, data?: TPipeline });
    const hideUpStatus = () => setUpStatus({ open: false, data: undefined });

    const [history, setHistory] = useState({ open: false, data: undefined } as { open: boolean, data?: TPipeline });
    const hideHistory = () => setHistory({ open: false, data: undefined });

    const [scheduleEci, setScheduleEci] = useState({ data: undefined, open: false } as { data?: TPipeline, open: boolean });
    const hideScheduleEciDialog = () => {
        setScheduleEci({ data: undefined, open: false });
    };

    const [confirmECI, setConfirmECI] = useState({ data: undefined, open: false } as { data?: TPipeline, open: boolean });
    const hideConfirmEciDialog = () => {
        setConfirmECI({ data: undefined, open: false });
    };

    const yesScheduleECI = () => {
        hideConfirmEciDialog();
        if (confirmECI.data) scheduleECI(confirmECI.data);
    }
    const dontScheduleECI = () => {
        hideConfirmEciDialog();
        setUpStatus({ open: true, data: confirmECI.data });
    }
    const scheduleECI = (rowData: TPipeline) => {
        if (rowData.requirementId && rowData.submissionId) setScheduleEci({ data: rowData, open: true });
    };

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTable(prev => ({ ...prev, loading: true }));
        const result = await Fetch<TL>("Pipeline",
            {
                query: new URLSearchParams({
                    lazyParams: JSON.stringify(lazyParams),
                    filters: JSON.stringify({
                        ...filters,
                        fromDate: filters.fromDate ? format(filters.fromDate as Date, 'yyyy-MM-dd') : null,
                        toDate: filters.toDate ? format(filters.toDate as Date, 'yyyy-MM-dd') : null
                    }),
                    ...(globalSearch ? { search: globalSearch } : {})
                }).toString()
            });
        if (result) {
            setTable({ data: result.data, count: result.count, loading: false });
        }
        setTable(prev => ({ ...prev, loading: false }));
    }


    useEffect(() => {
        loadTableData();
    }, [globalSearch, lazyParams, filters]);

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">Manage Pipeline</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <Search setSearch={setGlobalSearch} />
                <Button icon="pi pi-upload" className="p-button-help ml-2" onClick={exportCSV} />
            </span>
        </div>
    );

    return (
        <>
            <Accordion>
                <AccordionTab header="Filter">
                    <Filter filters={filters} setFilters={(filters: TPipFilter) => setFilters(filters)} loading={table.loading} />
                </AccordionTab>
            </Accordion>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small"
                value={table.data} lazy dataKey="id"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={table.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                emptyMessage="No Requirement found."
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={table.loading}
            >
                <Column field="id" header="Req ID"
                    body={(r: TPipeline) => (
                        <span onClick={() => setRequirementId(r.requirementId)}
                            title="Submit Candidate"
                            className="clickable-id">
                            #{r.requirementId}
                        </span>
                    )}
                />
                <Column field="jobTitle" header="Job Title" />
                <Column field="salesManager" header="Sales Manager" />
                <Column field="recruiter" header="Recruiter" />
                <Column field="candidateName" header="Candidate" />
                <Column field="skillCategory" header="Skill Category" />
                <Column field="vendorCompanyName" header="PrimeVendor" />
                <Column field="subVendorCompanyName" header="SubVendor" />
                <Column field="statusName" header="Status" body={(p: TPipeline) => {
                    if (!p.status_IsPositive || p.statusType > SubStatusType.Pipeline) return <span>{p.statusName}</span>
                    return p.statusName === "Submitted to End Client" ?
                        <span style={{ color: 'blueviolet', cursor: 'pointer' }} onClick={() => setConfirmECI({ open: true, data: p })}>{p.statusName}</span>
                        : <span style={{ color: 'blueviolet', cursor: 'pointer' }} onClick={() => setUpStatus({ open: true, data: p })}>{p.statusName}</span>
                }} />
                <Column field="postedOn" header="Posted On" body={(r: TPipeline) => format(new Date(r.postedOn + 'z'), "MM-dd-yyyy hh:mm a")} />
                <Column field="submital_Date" header="Submitted On" body={(r: TPipeline) => format(new Date(r.submital_Date + 'z'), "MM-dd-yyyy hh:mm a")} />
                <Column field="action" header="Action" body={(r: TPipeline) => (
                    <Button icon="pi pi-history" className="p-button-sm p-button-text" onClick={() => setHistory({ open: true, data: r })} />
                )} />
            </DataTable>
            <Dialog visible={confirmECI.open} header="Schedule ECI ?" modal className="p-fluid" onHide={hideConfirmEciDialog}>
                <div style={{ display: 'flex' }}>
                    <Button icon='pi pi-check' onClick={yesScheduleECI} label="YES" className="p-button-raised p-button-success m-2" />
                    <Button icon='pi pi-times' onClick={dontScheduleECI} label="NO" className="p-button-raised p-button-danger m-2" />
                </div>
            </Dialog>
            <RequirementInfo requirementId={requirementId} hideDialog={() => setRequirementId(null)} />
            <ChangeStatus data={upStatus.data} hideDialog={hideUpStatus} open={upStatus.open} reload={loadTableData} />
            <Form rData={scheduleEci.data as any} data={{ ...EmptyECI, submissionId: scheduleEci.data?.submissionId ?? 0 }} hideDialog={hideScheduleEciDialog} open={scheduleEci.open} reload={loadTableData} />
            <PipelineHistory data={history.data} hideDialog={hideHistory} open={history.open} />
        </>
    );
}
